import {Faker, faker} from "@faker-js/faker";
import {Address, BasisType, BasisTypeName, Booking, CustomerType, DKK, Goal, IServiceCombinations, Partner, PrivateCustomer, RRDate, Service, basisTypeNames} from "@raskrask-dk/raskrask-utils";
import type {IGoalDTO, IPartnerDTO, IPeriodDTO, IServiceDTO} from "../interfaces";

class ModelFakeFactory {
  private _faker: Faker;
  static goalID = 1; // tracks last goalID - avoids collision
  static serviceId = 1; // tracks last serviceID - avoids collision
  static partnerId = 1; // tracks last partnerID - avoids collision

  constructor() {
    this._faker = faker;
    this._faker.seed(42);
  }

  public number({min, max}: {min?: number; max?: number}): number {
    return this._faker.datatype.number({min, max});
  }

  public randomElements<T>(array: T[], count?: number): T[] {
    return this._faker.helpers.arrayElements(array, count);
  }

  public makeAddress(): Address {
    const address = this._faker.address.streetAddress();
    const geoLat = this._faker.address.latitude(57.72093, 54.76906);
    const geoLng = this._faker.address.longitude(14.70664, 8.24402);
    const zipCode = parseInt(this._faker.address.zipCode("####"));
    const floor = this._faker.datatype.number({min: 0, max: 15});
    return new Address(address, geoLat, geoLng, zipCode, floor.toString());
  }

  public makeBasisType({
    basisTypeName,
    bookableBy = ["Private"],
  }: {
    basisTypeName: BasisTypeName;
    disabled?: boolean;
    bookableBy?: CustomerType[];
  }): BasisType {
    const id = basisTypeNames.indexOf(basisTypeName);
    const basisType = new BasisType(basisTypeName, id);
    bookableBy.forEach((customerType: CustomerType) => basisType.addBookableBy(customerType));
    return basisType;
  }

  public makeBasisTypes(): BasisType[] {
    return basisTypeNames.reduce((basisTypes: BasisType[], name: BasisTypeName) => {
      const thisBasisType = this.makeBasisType({basisTypeName: name});
      return [...basisTypes, thisBasisType];
    }, []);
  }

  public makeService(): Service {
    const id = this._faker.datatype.number({min: 1, max: 10});
    const name = this._faker.commerce.productName();
    const duration = this._faker.datatype.number({min: 1, max: 10});
    const freeConsulation = this._faker.datatype.boolean();
    return new Service(id, name, duration, freeConsulation);
  }

  public makePrice(): DKK {
    return this._faker.datatype.number({min: 400, max: 600});
  }

  public makePartner(): Partner {
    const id = this._faker.datatype.number({min: 1, max: 10});
    const alias = this._faker.name.firstName();
    return new Partner(id, alias);
  }

  public makeBooking({service, address, partner}: {service: Service; address: Address; partner: Partner}): Booking {
    const serviceDate = new RRDate(this._faker.date.future().toISOString());
    const problemDescription = this._faker.lorem.sentence();
    return new Booking(service, address, partner, serviceDate, problemDescription);
  }

  public makeGoal(): Goal {
    const id = this._faker.datatype.number({min: 1, max: 1000});
    const name = this._faker.commerce.productName();
    return new Goal(id, name);
  }

  public makePrivateCustomer(): PrivateCustomer {
    const email = this._faker.internet.email();
    const name = this._faker.name.findName();
    const phone = parseInt(this._faker.phone.phoneNumber("########"));
    const customer = new PrivateCustomer(name, email, phone, true);

    return customer;
  }

  public makeGoalDTOs(count: number): IGoalDTO[] {
    return Array.from(Array(count)).map(() => this.makeGoalDTO({}));
  }

  public makeGoalDTO(dto: Partial<IGoalDTO>): IGoalDTO {
    const id = ModelFakeFactory.goalID++;
    return {
      id: id,
      name: this._faker.word.conjunction(3),
      serviceIds: [],
      ...dto,
    };
  }

  public makeServiceDTO(): IServiceDTO {
    const partnerCount = this._faker.random.numeric(2);
    const serviceId = ModelFakeFactory.serviceId++;
    const durations = [45, 60, 90, 120];
    const goal = new Goal(1, "Goal");
    goal.addBasisTypeId(1);
    return {
      id: serviceId,
      name: this._faker.company.bsBuzz(),
      profileIds: Array(partnerCount).map(() => parseInt(this._faker.random.numeric(1))),
      packages: [],
      duration: this._faker.helpers.arrayElement(durations),
      isFreeConsultation: this._faker.datatype.boolean(),
      goal: goal,
    };
  }

  public makeServiceDTOs(count: number): IServiceDTO[] {
    return Array.from(Array(count)).map(() => this.makeServiceDTO());
  }

  public makePartnerDTO(dto: Partial<IPartnerDTO>): IPartnerDTO {
    const partnerId = ModelFakeFactory.partnerId++;
    const schedules: IPeriodDTO[] = this.makeSchedules(10);

    return {
      id: partnerId,
      name: this._faker.name.firstName(),
      offers: [],
      schedules: schedules,
      profileTexts: [],
      reviews: [],
      education: this._faker.lorem.sentence(10),
      gdprLink: "",
      tools: [],
      profilePictureLink: this._faker.image.people(200),
      ...dto,
    };
  }

  public makeSchedules(count: number, from: RRDate = new RRDate(), to: RRDate = new RRDate().plus({hour: 6})): IPeriodDTO[] {
    return Array.from(Array(count)).map(() => this.makeSchedule(from.plus({day: 1}), to.plus({day: 1})));
  }

  public makeSchedule(from: RRDate = new RRDate(), to: RRDate = new RRDate().plus({hour: 6})): IPeriodDTO {
    return {
      timezone: "Europe/Copenhagen",
      format: "yyyy-MM-dd HH:mm:ss",
      from: from.toString(),
      to: to.toString(),
    };
  }

  public makePartnerDTOs(count: number): IPartnerDTO[] {
    return Array.from(Array(count)).map(() => this.makePartnerDTO({}));
  }

  makeServiceCombinations(): IServiceCombinations {
    const service = {
      serviceTypeId: 1,
      serviceTypeName: "Massage",
      duration: 45,
      normalPrice: 498,
      isFreeConsultation: false,
    };

    return {
      serviceTypes: {
        1: service,
      },
      goals: {},
      basisTypes: {},
      bookingReasons: {},
    };
  }
}

export {ModelFakeFactory};
